/**
 * Demo Services.
 *
 * @format
 */

import React, { lazy, Suspense } from 'react';
import { createRoot } from 'react-dom/client';
import Loader from '@components/loader';

const DemoNavigation = lazy(
  () => import(/* webpackChunkName: "demo-navigation" */ './demo-navigation')
);

/**
 * Full List of Demos.
 */
export const fullDemoList = ['three-column-icon'];

/**
 * Load Highlight.js
 */
export const loadHighlightJs = (): void => {
  const css: HTMLLinkElement = document.createElement('link');
  css.rel = 'stylesheet';
  css.href =
    'https://cdnjs.cloudflare.com/ajax/libs/highlight.js/11.2.0/styles/github-dark.min.css';
  css.id = 'highlightjs-css';
  document.body.appendChild(css);

  const js: HTMLScriptElement = document.createElement('script');
  js.src =
    'https://cdnjs.cloudflare.com/ajax/libs/highlight.js/11.2.0/highlight.min.js';
  js.id = 'highlightjs';
  document.body.appendChild(js);

  // Load HLJS
  js.onload = () => {
    if (window.hljs) {
      window.hljs.highlightAll();
    }
  };
};

/**
 * Copy Source Text.
 *
 * @format
 * @param {string} copySource
 */

export const copyToClipboard = (copySourceText: string): void => {
  const copyElement: HTMLTextAreaElement = document.createElement('textarea');
  copyElement.value = copySourceText;
  copyElement.setAttribute('readonly', '');
  copyElement.style.position = 'absolute';
  copyElement.style.left = '-9999px';
  document.body.appendChild(copyElement);
  copyElement.select();
  document.execCommand('copy');
  document.body.removeChild(copyElement);
};

/**
 * Demos.
 */
export const demos = (): void => {
  /**
   * Add Demo Navigation
   */
  const addDemoNavigation = (): void => {
    const demoContainer: HTMLElement | null = document.getElementById('demo');
    if (demoContainer) {
      const page = demoContainer.dataset.page;
      const root = createRoot(demoContainer);
      root.render(
        <Suspense fallback={<Loader />}>
          <DemoNavigation page={page} />
        </Suspense>
      );
    }
  };

  /**
   * Load Demo.
   *
   * @param demoClass
   * @returns
   */
  const loadDemo = (demoClass: string, demoList: Array<string>) => {
    let component = null;
    if (demoClass) {
      demoList.forEach((demo) => {
        if (demoClass.indexOf(demo) > -1) {
          const componentPath = `./blade-demos/${demo}-demo`;
          component = lazy(() =>
            import(
              /* webpackChunkName: "[request]" */ `${componentPath}`
            ).catch(() => console.warn(`Error importing: ${componentPath}`))
          );
        }
      });
    }
    return component;
  };

  /**
   * Demo Builder.
   */
  const demoBuilder = (): void => {
    const demos: NodeListOf<HTMLElement> =
      document.querySelectorAll('.blade-demo');

    if (demos && demos.length > 0) {
      demos.forEach((demo) => {
        demo.classList.forEach((name) => {
          if (name.indexOf('-demo') > -1) {
            const DemoComponent = loadDemo(name, fullDemoList);
            const root = createRoot(demo);
            if (DemoComponent) {
              root.render(
                <Suspense fallback={<Loader />}>
                  <DemoComponent />
                </Suspense>
              );
            }
          }
        });
      });
    }
  };

  /**
   * Setup Demo Page.
   */
  const setupDemoPage = (): void => {
    document.body.classList.add('demo-page');
    addDemoNavigation();
    demoBuilder();
  };
  setupDemoPage();
};
